import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NbDialogService, NbLayoutScrollService } from "@nebular/theme";
import { replace, remove, clone, find } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { User } from "../../@core/data/users";
import { UserService } from "../../@core/services/user.service";
import { convertTimestamp } from "../../@core/utils/convertTimestamps.service";
import Announcement from "../../models/announcement.model";

import Comment from "../../models/comment.model";
import Course from "../../models/course.model";
import Enrollment from "../../models/enrollment.model";
import Exercise from "../../models/exercise.model";
import Feedback from "../../models/feedback.model";
import LectureContent from "../../models/lecture-content.model";
import { WhereList } from "../../models/pagination";
import Quiz from "../../models/quiz.model";
import ReportSpam from "../../models/report-spam.model";
import { CommentService } from "../../services/comment.service";
import { DialogYesNoPromptComponent } from "../dialog-yesno-prompt/dialog-yesno-prompt.component";

@Component({
  selector: "ngx-view-comments",
  styleUrls: ["./view-comments.component.scss"],
  templateUrl: "./view-comments.component.html",
})
export class ViewCommentsComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();

  @Input() course? = new Course();
  @Input() lecture? = new LectureContent();
  @Input() exercise? = new Exercise();
  @Input() quiz? = new Quiz();
  @Input() reported? = new ReportSpam();
  @Input() feedback? = new Feedback();
  @Input() announcement? = new Announcement();
  @Input() student? = new Enrollment();

  @Input() mode?: string = ""; //course - other - student
  @Output() onClose: EventEmitter<any> = new EventEmitter();

  refTitle: string = "";
  refUid: string = "";
  newComment = true;

  replyForm: FormGroup;
  submitted = false;
  loading = false;

  comments?: Comment[] = [];
  reply?: Comment = new Comment();
  wheres: WhereList[] = [];

  isLoggedIn = false;
  uid = "anonymous";
  pageSize = 10;
  cursor: any;
  done = false;

  posX = 0;
  posY = 0;

  constructor(
    private fb: FormBuilder,
    private commentService: CommentService,
    public userService: UserService,
    private dialogService: NbDialogService,
    private scrollService: NbLayoutScrollService
  ) {}

  ngOnInit() {
    this.replyForm = this.fb.group({
      content: Validators.required,
    });

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user && user.uid != this.uid) {
          this.uid = user.uid;
          this.isLoggedIn = true;
        } else {
          this.uid = "";
        }
      });

    this.scrollService
      .getPosition()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.posX = data.x;
        this.posY = data.y;
      });

    this.initComment();
    this.getComments();
  }

  ngOnDestroy() {
    //console.log("Items destroyed");
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMore() {
    this.pageSize = 12;
    this.getComments();
  }

  createReply() {
    this.loading = true;
    this.submitted = true;

    this.reply.content = this.reply.content.trim();
    this.commentService
      .create(this.reply)
      .then((value) => {
        this.calculateNoComment(1);

        const newReply = clone(value);
        this.comments = [newReply, ...this.comments];

        this.initComment();
        this.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        this.loading = false;
        this.submitted = false;
      });
  }

  deleteReply(item) {
    this.dialogService
      .open(DialogYesNoPromptComponent, {
        context: {
          title: "Bạn có chắc xóa nội dung này?",
        },
      })
      .onClose.subscribe((value) => {
        if (value) {
          this.submitted = true;
          this.loading = true;
          this.submitted = true;

          this.commentService
            .delete(item.id)
            .then((result) => {
              this.calculateNoComment(-1);

              remove(this.comments, (e) => {
                return e.id == item.id;
              });
              this.loading = false;
              this.submitted = false;
            })
            .catch((err) => {
              this.loading = false;
              this.submitted = false;
            });
        }
      });
  }

  //Editing
  content = "";
  onEditReply(item: Comment) {
    item.editing = true;
    this.content = item.content;
  }

  editReply(item: Comment) {
    item.loading = true;
    this.submitted = true;
    item.content = this.content;

    this.commentService
      .update(item)
      .then((data) => {
        item.editing = false;

        item.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        item.editing = false;
        item.loading = false;
        this.submitted = false;
      });
  }

  cancelReply(item: Comment) {
    item.editing = false;
  }

  report(item: ReportSpam) {
    remove(this.comments, (e) => {
      return e.id == item.refId;
    });
  }

  onNewReply(item: Comment) {
    if (item) {
      this.reply.toId = item.id;
      this.reply.toUid = item.createdBy;
      this.reply.toAuthor = item.author;
      this.reply.refTitle = item.content;
      this.reply.level = (item.level ? item.level : 0) + 1;
    } else {
      this.initComment();
    }

    this.scrollService.scrollTo(this.posX, this.posY);
  }

  closeReplies() {
    this.onClose.emit(true);
  }

  readMoreRefTitle(item) {
    item.moreRefTitle = item.moreRefTitle == true ? false : true;
  }

  private initComment() {
    this.reply = new Comment();
    this.reply.level = 0;
    this.reply.mode = "";
    this.reply.refId = "";

    if (this.course && this.course.id) {
      this.reply.mode = "courses";
      this.reply.refId = this.course.id;
      this.reply.refTitle = this.course.title;
      this.reply.refUid = this.course.createdBy;
      this.reply.refAuthor = this.course.author;

      this.reply.privacy = this.course.privacy;
      this.reply.toUid = this.course.createdBy;
      this.reply.toAuthor = this.course.author;
      this.reply.refUrl = `/course/${this.course.id}`;

      this.refUid = this.course.createdBy;
      this.refTitle = this.course.title;
    } else if (this.lecture && this.lecture.id) {
      this.reply.mode = "lecture-contents";
      this.reply.refId = this.lecture.id;
      this.reply.refTitle = this.lecture.title;
      this.reply.refUid = this.lecture.createdBy;
      this.reply.refAuthor = this.lecture.author;

      this.reply.privacy = this.lecture.privacy;
      this.reply.toUid = this.lecture.createdBy;
      this.reply.toAuthor = this.lecture.author;
      this.reply.refUrl = `/lecture/${this.lecture.id}`;

      this.refUid = this.lecture.createdBy;
      this.refTitle = this.lecture.title;
      this.newComment = !this.lecture.newComment ? false : true;
    } else if (this.exercise && this.exercise.id) {
      this.reply.mode = "exercises";
      this.reply.refId = this.exercise.id;
      this.reply.refTitle = this.exercise.title;
      this.reply.refUid = this.exercise.createdBy;
      this.reply.refAuthor = this.exercise.author;

      this.reply.privacy = this.exercise.privacy;
      this.reply.toUid = this.exercise.createdBy;
      this.reply.toAuthor = this.exercise.author;
      this.reply.refUrl = `/exercise/${this.exercise.id}`;

      this.refUid = this.exercise.createdBy;
      this.refTitle = this.exercise.title;
      this.newComment = !this.exercise.newComment ? false : true;
    } else if (this.quiz && this.quiz.id) {
      this.reply.mode = "quizzes";
      this.reply.refId = this.quiz.id;
      this.reply.refTitle = this.quiz.title;
      this.reply.refUid = this.quiz.createdBy;
      this.reply.refAuthor = this.quiz.author;

      this.reply.privacy = this.quiz.privacy;
      this.reply.toUid = this.quiz.createdBy;
      this.reply.toAuthor = this.quiz.author;
      this.reply.refUrl = `/quiz/${this.quiz.id}`;

      this.refUid = this.quiz.createdBy;
      this.refTitle = this.quiz.title;
    } else if (this.reported && this.reported.id) {
      this.reply.mode = "report-spams";
      this.reply.refId = this.reported.id;
      this.reply.refTitle = this.reported.refTitle;
      this.reply.refUid = this.reported.createdBy;
      this.reply.refAuthor = this.reported.author;

      this.reply.privacy = true;
      this.reply.toUid = this.reported.createdBy;
      this.reply.toAuthor = this.reported.author;
      this.reply.refUrl = `/reported/${this.reported.id}`;

      this.refUid = this.reported.createdBy;
      this.refTitle = this.reported.refTitle;
    } else if (this.feedback && this.feedback.id) {
      this.reply.mode = "feedbacks";
      this.reply.refId = this.feedback.id;
      this.reply.refTitle = this.feedback.title;
      this.reply.refUid = this.feedback.createdBy;
      this.reply.refAuthor = this.feedback.author;

      this.reply.privacy = true;
      this.reply.toUid = this.feedback.createdBy;
      this.reply.toAuthor = this.feedback.author;
      this.reply.refUrl = `/feedback/${this.feedback.id}`;

      this.refUid = this.feedback.createdBy;
      this.refTitle = this.feedback.title;
    } else if (this.announcement && this.announcement.id) {
      this.reply.mode = "announcements";
      this.reply.refId = this.announcement.id;
      this.reply.refTitle = this.announcement.title;
      this.reply.refUid = this.announcement.createdBy;
      this.reply.refAuthor = this.announcement.author;

      this.reply.privacy = true;
      this.reply.toUid = this.announcement.createdBy;
      this.reply.toAuthor = this.announcement.author;
      this.reply.refUrl = `/announcement/${this.announcement.id}`;

      this.refUid = this.announcement.createdBy;
      this.refTitle = this.announcement.title;
      this.newComment = !this.announcement.newComment ? false : true;
    } else if (this.student && this.student.id) {
      this.reply.mode = "enrollments";
      this.reply.refId = this.student.id;
      this.reply.refTitle = this.student.course.title;
      this.reply.refUid = this.student.createdBy;
      this.reply.refAuthor = this.student.author;

      this.reply.privacy = true;
      this.reply.toUid = this.student.createdBy;
      this.reply.toAuthor = this.student.author;
      this.reply.refUrl = `/student/courses/${this.student.id}/${this.student.courseId}`;

      this.refUid = this.student.createdBy;
      this.refTitle = this.student.author.displayName;
    }
  }

  private getComments(): void {
    if (this.reply.mode != "" && this.reply.refId != "") {
      this.loading = true;
      this.wheres = [];
      this.wheres.push({
        fieldPath: "mode",
        value: this.reply.mode,
      });
      this.wheres.push({
        fieldPath: "refId",
        value: this.reply.refId,
      });

      this.commentService
        .getComments(this.wheres, this.pageSize, this.cursor)
        .then((querySnapshot) => {
          if (querySnapshot.docs.length > 0) {
            this.cursor = querySnapshot.docs[querySnapshot.docs.length - 1];

            querySnapshot.forEach((doc) => {
              let data = new Comment();
              data = doc.data();
              data.id = doc.id;
              data.createdOn = convertTimestamp(doc.data().createdOn);
              data.level = data.level ? data.level : 0;
              data.moreRefTitle = false;

              let item = find(this.comments, ["id", data.id]);
              if (item) {
                item = data;
              } else {
                this.comments.push(data);
              }
            });
          } else {
            this.cursor = null;
            this.done = true;
          }

          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
        });
    }
  }

  private calculateNoComment(value) {
    if (this.course && this.course.id) {
      this.course.noComment += value;
    } else if (this.lecture && this.lecture.id) {
      this.lecture.noComment += value;
    } else if (this.exercise && this.exercise.id) {
      this.exercise.noComment += value;
    } else if (this.quiz && this.quiz.id) {
      this.quiz.noComment += value;
    } else if (this.reported && this.reported.id) {
      this.reported.noComment += value;
    } else if (this.feedback && this.feedback.id) {
      this.feedback.noComment += value;
    } else if (this.announcement && this.announcement.id) {
      this.announcement.noComment += value;
    } else if (this.student && this.student.id) {
      this.student.noComment += value;
    }
  }
}
